// src/features/screener/api.ts
//
// Typed client for the multi-dimensional Global Screener endpoints:
//   GET  /api/v1/global-search/screener/options  -> dropdown option lists
//   POST /api/v1/global-search/screener/search   -> filtered result set
//
// The TypeScript contracts below mirror the backend Pydantic models in
// backend/app/domain/global_search_screener/schemas.py
// (ScreenerOptions / ScreenerRequest / ScreenerResultDTO / ScreenerResponse)
// — keep the two in sync when the API changes.
//
// AUTH: we deliberately go through the shared axios `apiClient` (not a
// raw fetch) so the request automatically carries the Forge Ultra JWT
// (Authorization: Bearer) AND the `forge_session` cookie, and so a 401
// flows through the central silent-refresh / bounce-to-WP handling in
// api/client.ts — exactly like every other authenticated call in the SPA.

import { apiClient } from '../../api/client';

// Routing discriminant — matches the backend AssetClass enum. Drives
// which deep-dive page a clicked result row navigates to.
export type AssetClass = 'equity' | 'etf' | 'reit' | 'crypto';

// The dynamic dropdown lists (sectors + exchanges) the backend derives
// straight from the in-memory Polars frame, so they always mirror what
// the screener can actually filter on.
export interface ScreenerOptions {
    sectors: string[];
    exchanges: string[];
}

// The filter payload. Every field is optional — only the ones the user
// actually set are sent (the page strips `undefined`s before POSTing).
// `market_cap` and `price` support a two-sided range.
export interface ScreenerFilters {
    market_cap_more_than?: number;
    market_cap_lower_than?: number;
    sector?: string;
    beta_more_than?: number;
    price_more_than?: number;
    price_lower_than?: number;
    dividend_more_than?: number;
    volume_more_than?: number;
    exchange?: string;
    is_etf?: boolean;
}

// One row returned for the results table. Numeric descriptors are
// null/absent for the breadth-layer symbols the sync hasn't enriched.
export interface ScreenerResult {
    symbol: string;
    name: string;
    asset_class: AssetClass;
    exchange?: string | null;
    sector?: string | null;
    // Native-currency market cap (raw FMP figure, in the listing's own
    // currency). Kept for reference; the grid shows `market_cap_usd` instead.
    market_cap?: number | null;
    // USD-normalised market cap — what the grid DISPLAYS and the backend
    // SORTS/filters "market cap" on, so "largest companies" is a true global
    // comparison regardless of listing currency. Null when unknown / no FX.
    market_cap_usd?: number | null;
    price?: number | null;
    beta?: number | null;
    volume?: number | null;
    dividend?: number | null;
    // ISO listing currency resolved server-side (EUR for XETRA/Vienna rows).
    currency?: string | null;
}

export interface ScreenerResponse {
    results: ScreenerResult[];
    total_matches: number;
    execution_time_ms: number;
}

// Numeric columns the backend can sort the FULL filtered universe by
// (mirrors the ScreenerSortField Literal in schemas.py). The sort runs
// server-side BEFORE the limit, so the returned page is the true top-N
// by the chosen column — not a re-sort of a market-cap-ranked page.
export type ScreenerSortKey =
    | 'market_cap'
    | 'price'
    | 'beta'
    | 'dividend'
    | 'volume';

export interface ScreenerSort {
    sortBy: ScreenerSortKey;
    sortDesc: boolean;
}

/**
 * Fetch the unique sector + exchange option lists that populate the
 * filter dropdowns. Fails SILENTLY (returns empty lists) on any non-auth
 * error so the UI degrades gracefully rather than throwing. A 401 is NOT
 * swallowed — it propagates into the apiClient interceptor which owns the
 * silent-refresh / re-login flow.
 */
export async function fetchScreenerOptions(): Promise<ScreenerOptions> {
    try {
        const { data } = await apiClient.get<ScreenerOptions>(
            '/global-search/screener/options',
        );
        return {
            sectors: data.sectors ?? [],
            exchanges: data.exchanges ?? [],
        };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[screener] options request failed', err);
        return { sectors: [], exchanges: [] };
    }
}

/**
 * Run the screener. Sends ONLY the provided filter fields as a JSON body
 * plus the pagination `limit`. Throws on error so the caller can surface
 * a ret/loading state; a 401 propagates into the interceptor as above.
 *
 * @param filters The active (defined) filter fields.
 * @param limit   Max rows for the table (backend caps at 500; default 50).
 * @param sort    Optional server-side sort column + direction.
 * @param signal  Optional AbortSignal — lets the caller physically sever the
 *                HTTP connection when a newer keystroke supersedes this
 *                request, so FastAPI never wastes CPU on an abandoned search.
 *                Axios raises a CanceledError (code ERR_CANCELED) on abort,
 *                which the caller filters out as a non-error.
 */
export async function runScreener(
    filters: ScreenerFilters,
    limit = 50,
    sort?: ScreenerSort,
    signal?: AbortSignal,
): Promise<ScreenerResponse> {
    const { data } = await apiClient.post<ScreenerResponse>(
        '/global-search/screener/search',
        {
            ...filters,
            limit,
            ...(sort
                ? { sort_by: sort.sortBy, sort_desc: sort.sortDesc }
                : {}),
        },
        { signal },
    );
    return {
        results: data.results ?? [],
        total_matches: data.total_matches ?? 0,
        execution_time_ms: data.execution_time_ms ?? 0,
    };
}
